import DynamicFieldsHandler from "../dynamicFieldsHandler";
import {
  ApiMessageType,
  IApiEndPointDataSection, IDynamicApiDynamicInputsConfig,
  IDynamicDataConfig, IDynamicObjectModelPropertyConfig,
  IDynamicTreeContext,
} from '~/interfaces/dynamicFieldsInterfaces'
import {DynamicReferenceType} from "~/enums/dynamicReferenceType";
import {ReferencePathNode} from "~/models/referencePaths/referencePathNode";
import {
  DeterministicDynamicComponentType,
  IApiEndPointFieldsHandlerConfig,
  IDeterministicDynamicFieldsHandler,
  TModes
} from "~/interfaces/dynamicFieldsElementOptionsInterfaces"
import {escape} from "lodash";
import {assignProcessFlowDynamicFieldsCommonOptions} from '~/models/dynamicFields/helpers'
import {BooleanHelper} from '~/helpers/booleanHelper'
import {IProcessFlowVariable} from '~/interfaces/processFlowInterfaces'
import ProcessFlowDynamicFieldHandlerSelectMixin from '~/models/dynamicFields/handlers/mixins/processFlowDynamicFieldHandlerSelectMixin'
import EndPoint from "../../../../../formability/organization_settings/models/connections/endPoint";
import DynamicInput from "~/models/dynamicInput";
import ApiEndPointOptions from "~/models/dynamicFields/options/apiEndpointOptions";
import {
  CertificateDynamicInputType
} from '../../../../../formability/certificateBuilder/enums/certificateDynamicInputType'

type ItemTypes = EndPoint | DynamicInput

export default class ProcessFlowApiEndPointDynamicFieldsHandler extends DynamicFieldsHandler implements IDeterministicDynamicFieldsHandler<ItemTypes>, IApiEndPointFieldsHandlerConfig {
  COMPONENTS: Array<DeterministicDynamicComponentType> = ['process-flow-api-end-point-dynamic-fields'];
  variable: IProcessFlowVariable
  dynamicMessage: string;
  showDynamicMessage: boolean;
  mode: TModes;
  apiMessageType: ApiMessageType
  endPoint: EndPoint;
  allowApiEndpointSelection: boolean = true;
  header: string = 'Body';
  rootHeader: string;
  elementOptions = new ApiEndPointOptions();
  destinationIsCollection: boolean | null = null;
  restrictToTopLevelCollections: boolean | null = null
  restrictToRootModel: boolean | null = null

  constructor(options: IApiEndPointFieldsHandlerConfig) {
    super(options);
    this.mode = options.mode || 'flat';
    this.endPoint = options.endPoint;
    this.apiMessageType = options.apiMessageType
    if (BooleanHelper.isStrictBoolean(options.allowApiEndpointSelection)) this.allowApiEndpointSelection = options.allowApiEndpointSelection;
    if (options.header) this.header = options.header;
    if (options.rootHeader) this.rootHeader = options.rootHeader;
    if (options.elementOptions) {
      this.elementOptions = options.elementOptions
    }
    assignProcessFlowDynamicFieldsCommonOptions(this, options)
  }

  apiEndPointDataSection(q: string, endPoint: EndPoint, apiMessageType: ApiMessageType): IApiEndPointDataSection {
    if (endPoint.name.toLowerCase().match(q) !== null) {
      return {
        endPoint,
        apiMessageType,
        isOpen: true,
        select: this.selectApiEndPointData.bind(this),
      }
    }
    return null;
  }

  selectApiEndPointData(endPoint: EndPoint, path: ReferencePathNode[]): IDynamicDataConfig {
    const config: Omit<IDynamicDataConfig, 'id'> = {
      type: DynamicReferenceType.ApiEndpoint,
      label: escape(endPoint.name),
      path: path,
    };
    return new ProcessFlowDynamicFieldHandlerSelectMixin(this).select(endPoint, config);
  }

  dynamicInputsSection(q: string, endPoint: EndPoint, apiMessageType: ApiMessageType, objectDynamicInput: DynamicInput = null): Pick<IDynamicApiDynamicInputsConfig, 'properties' | 'select'> {
    const section: Pick<IDynamicApiDynamicInputsConfig, 'properties' | 'objects' | 'select'> = {
      properties: [],
      objects: [],
      select: this.selectDynamicInput.bind(this),
    };

    this.elementOptions.getElements({endPoint, apiMessageType, objectDynamicInput}).forEach((di) => {
      if (di.name.toLowerCase().match(q) === null) return

      if (di.type === CertificateDynamicInputType.Object) {
        section.objects.push(di)
      } else {
        section.properties.push(di)
      }
    })

    return section;
  }

  private selectDynamicInput(dynamicInput: DynamicInput, endPoint: EndPoint, apiMessageType: ApiMessageType, path: ReferencePathNode[]): IDynamicDataConfig {
    const config: Omit<IDynamicDataConfig, 'id'> = {
      type: DynamicReferenceType.DynamicInput,
      rootId: endPoint.id,
      pillColor: this.pillColor,
      path: path,
      label: dynamicInput.name,
    };

    return new ProcessFlowDynamicFieldHandlerSelectMixin(this).select(dynamicInput, config);
  }

  headerInputsSection(q: string, endPoint: EndPoint) {
    const section = {
      headers: [],
      select: this.selectHeader.bind(this)
    }

    endPoint.allLiveHeaders.forEach(headerPair => {
      if (headerPair.key.toLowerCase().match(q) === null) return

      section.headers.push(headerPair)
    })
    return section
  }

  selectHeader(header, endPoint, path) {
    const config: Omit<IDynamicDataConfig, 'id'> = {
      type: DynamicReferenceType.ApiHeader,
      rootId: endPoint.id,
      pillColor: this.pillColor,
      path: path,
      label: header.key,
    };

    return new ProcessFlowDynamicFieldHandlerSelectMixin(this).select(header, config);
  }

  URLParametersSection(q: string, endPoint: EndPoint) {
    const section = {
      URLParameters: [],
      select: this.selectURLParameter.bind(this)
    }

    endPoint.URLparameters.forEach(URLparameter => {
      if (URLparameter.toLowerCase().match(q) === null) return

      section.URLParameters.push({
        name: URLparameter
      })
    })
    return section
  }

  selectURLParameter(URLParameter, endPoint, path) {
    const config: Omit<IDynamicDataConfig, 'id'> = {
      type: DynamicReferenceType.ApiURLParameter,
      rootId: endPoint.id,
      pillColor: this.pillColor,
      path: path,
      label: URLParameter.name,
    };

    return new ProcessFlowDynamicFieldHandlerSelectMixin(this).select(URLParameter, config);
  }

  isDisabled(item: ItemTypes, context: Partial<IDynamicTreeContext<ItemTypes>> = {}): boolean {
    if (item instanceof DynamicInput) {
      if (item.type === CertificateDynamicInputType.Static && !this.staticVariableAllowed) return true
    }

    if (this.restrictToRootModel) {
      if (!(item instanceof EndPoint)) return true
    }

    return false
  }

  get staticVariableAllowed(): boolean {
    return false
  }

  protected itemIsCollection(item: ItemTypes, context: Partial<IDynamicTreeContext<ItemTypes>> = {}): boolean {
    if (!this.variable.is_single) return true;

    if (item instanceof DynamicInput) {
      return context.isTopLevelCollectionAssigned || item.isDataCollection;
    }

    return false;
  }

  protected apiEndPointIsCollection(item: EndPoint, context: Partial<IDynamicTreeContext<ItemTypes>> = {}): boolean {
    return !this.variable.is_single;
  }

  private itemListTypeMatches(item: ItemTypes, context: Partial<IDynamicTreeContext<ItemTypes>> = {}) {
    if (item instanceof EndPoint) {
      return true
    } else {
      return this.destinationIsCollection === this.itemIsCollection(item, context)
    }
  }

  isTopLevelCollection(item: ItemTypes, context: Partial<IDynamicTreeContext<ItemTypes>> = {}): boolean {
    // If top level is already assigned, exit early
    if (context.isTopLevelCollectionAssigned) return false

    if (item instanceof EndPoint) {
      if (!this.variable.is_single && this.variable.reference_id === item.id) return true
      if (this.variable.is_single && this.itemIsCollection(context.parent, context)) return true
    } else if (item instanceof DynamicInput) {
      return this.itemIsCollection(item, context);
    }

    return false
  }

  hasApiEndPointDataMatch(q: string, endPoint: EndPoint, apiMessageType: ApiMessageType): boolean {
    return this.dynamicInputsSection(q, endPoint, apiMessageType).properties.length > 0;
  }
}